import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import "./BookingList.css";
import { useDispatch, useSelector } from "react-redux";
import { fetchBookings } from "../../redux/actions/bookingActions";
import axios from "axios";
import MultiSelect from "../MultiSelect/MultiSelect";
import { updateBookingStatus } from "../../redux/reducers/bookingReducer"; // ✅ Import action
import { useNavigate } from "react-router-dom";

const BookingList = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const bookingsData = useSelector((state) => state.bookings.bookings);
	const username = useSelector((state) => state.auth.user?.username);
	const [refreshOnStatusUpdate, setRefreshOnStatusUpdate] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [displayBookings, setDisplayBookings] = useState([]);
	const bookingsRef = useRef([]);

	const formatDate = (dateString) => {
		const date = new Date(dateString);
		const options = { year: "numeric", month: "long", day: "2-digit" };
		return date.toLocaleDateString("en-GB", options); // Using 'en-GB' for dd month yyyy format
	};

	// Wrap bookings in useMemo to prevent unnecessary recreations
	const bookings = useMemo(() => 
		bookingsData.map(booking => ({
			...booking,
			PurchaseDate: formatDate(booking.PurchaseDate)
		})), 
		[bookingsData] // Only recreate when bookingsData changes
	);

	const [filters, setFilters] = useState({
		stripeid: [],
		title: [],
		duration: [],
		price: [],
		location: [],
		date: [],
		name: [],
		purchasedate: [],
		paidamount: [],
		Places: [],
		bookingstatus: [],
	});

	const region = username === "ukadmin" ? "United Kingdom" : "United States";
	const regionCsvfilter = username === "ukadmin" ? "uk" : "us";
	
	// Check if any filters are applied
	const isFiltered = Object.values(filters).some(
		(arr) => Array.isArray(arr) && arr.length > 0
	);

	const handleFilterChange = (name, selectedOptions) => {
		console.log(`Filter changed: ${name}`, selectedOptions);
		setFilters(prevFilters => {
			const newFilters = {
				...prevFilters,
				[name]: selectedOptions,
			};
			console.log("New filters:", newFilters);
			return newFilters;
		});
	};

	const formatOptions = (items) => {
		return Array.from(new Set(items)).map((item, index) => ({
			value: item,
			label: item,
			id: `${item}-${index}`
		}));
	};

	// Function to get filtered bookings
	const getFilteredBookings = useCallback(() => {
		console.log("Running getFilteredBookings with filters:", JSON.stringify(filters));
		return bookings.filter(
			(booking) =>
				(filters.stripeid.length === 0 ||
					filters.stripeid.some(
						(opt) => opt.value === booking.StripeOrderId
					)) &&
				(filters.duration.length === 0 ||
					filters.duration.some(
						(opt) => opt.value === booking.CourseDuration
					)) &&
				(filters.title.length === 0 ||
					filters.title.some(
						(opt) => opt.value === booking.CourseTitle
					)) &&
				(filters.location.length === 0 ||
					filters.location.some(
						(opt) => opt.value === booking.CourseCity
					)) &&
				(filters.name.length === 0 ||
					filters.name.some(
						(opt) => opt.value === booking.CustomerName
					)) &&
				(filters.purchasedate.length === 0 ||
					filters.purchasedate.some(
						(opt) => opt.value === booking.PurchaseDate
					)) &&
				(filters.date.length === 0 ||
					filters.date.some((opt) => opt.value === booking.Date)) &&
				(filters.bookingstatus.length === 0 ||
					filters.bookingstatus.some(
						(opt) => opt.value === booking.BookingStatus
					)) &&
				(filters.price.length === 0 ||
					filters.price.some((opt) => {
						const bookingPrice = (booking.CoursePrice / 100).toFixed(2);
						return parseFloat(opt.value) === parseFloat(bookingPrice);
					}))
		);
	}, [bookings, filters]);

	// City counts for summary tabs
	const getCityCounts = useCallback(() => {
		const counts = {
			london: bookings.filter(b => b.CourseCity === "London").length,
			manchester: bookings.filter(b => b.CourseCity === "Manchester").length,
			leeds: bookings.filter(b => b.CourseCity === "Leeds").length,
			beverlyhills: bookings.filter(b => b.CourseCity === "Beverly Hills").length,
			boston: bookings.filter(b => b.CourseCity === "Boston").length,
			scottsdale: bookings.filter(b => b.CourseCity === "Scottsdale").length,
			mcleanva: bookings.filter(b => b.CourseCity === "McLean VA").length,
			newyork: bookings.filter(b => b.CourseCity === "New York").length
		};
		return counts;
	}, [bookings]);

	const cityCounts = getCityCounts();

	// Fetch bookings data on component mount
	useEffect(() => {
		setIsLoading(true);
		dispatch(fetchBookings()).then(() => {
			setIsLoading(false);
		});
	}, [dispatch, refreshOnStatusUpdate]);

	// Update filtered bookings when bookings or filters change
	useEffect(() => {
		if (bookings && bookings.length > 0) {
			console.log("Calculating bookings with filters:", JSON.stringify(filters));
			const filtered = getFilteredBookings();
			bookingsRef.current = filtered;
			setDisplayBookings([...filtered]);
			console.log("Bookings updated:", filtered.length);
		}
	}, [bookings, getFilteredBookings, filters]);

	// Debugging information
	useEffect(() => {
		console.log("isFiltered:", isFiltered);
		console.log("Current filters:", JSON.stringify(filters));
	}, [isFiltered, filters]);

	const handleExportAll = async () => {
		try {
			// Create CSV from current filtered bookings
			const createCSV = (data) => {
				// Define headers
				const headers = [
					"Stripe ID", 
					"Course Title", 
					"Course Duration", 
					"Course Price", 
					"Course City", 
					"Date", 
					"Customer Name", 
					"Customer Email", 
					"Customer Address", 
					"Customer Phone", 
					"Purchase Date", 
					"Deposit Amount", 
					"Places",
					"Booking Status"
				];
				
				// Process rows
				const rows = data.map(booking => {
					// Parse address from JSON string
					let addressStr = '';
					try {
						const address = JSON.parse(booking.CustomerBillingAddress);
						addressStr = `${address.address?.line1 || ''}, ${address.address?.line2 ? address.address.line2 + ', ' : ''} ${address.address?.city || ''}, ${address.address?.state || ''} ${address.address?.postal_code || ''}, ${address.address?.country || ''}`;
					} catch (e) {
						addressStr = booking.CustomerBillingAddress || '';
					}
					
					// Format price as currency
					const price = (booking.CoursePrice / 100).toFixed(2);
					const deposit = (booking.DepositeAmount / 100).toFixed(2);
					
					return [
						booking.StripeOrderId || '',
						booking.CourseTitle || '',
						booking.CourseDuration || '',
						price || '',
						booking.CourseCity || '',
						booking.Date ? booking.Date.replace(/with.*/, "") : '',
						booking.CustomerName || '',
						booking.CustomerEmail || '',
						addressStr,
						booking.CustomerTelephone || '',
						booking.PurchaseDate || '',
						deposit || '',
						booking.Places || '',
						booking.BookingStatus || ''
					];
				});
				
				// Combine headers and rows
				return [headers, ...rows]
					.map(row => 
						row.map(value => 
							// Quote values with commas and escape quotes
							`"${String(value).replace(/"/g, '""')}"`
						).join(',')
					)
					.join('\n');
			};
			
			// Use filtered bookings or all bookings if no filters are applied
			const dataToExport = displayBookings.length > 0 ? displayBookings : bookings;
			
			// Create CSV content
			const csvContent = createCSV(dataToExport);
			
			// Create blob and download
			const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			const date = new Date().toISOString().slice(0, 10);
			
			link.href = url;
			link.download = `sassoon-bookings-${regionCsvfilter}-${date}.csv`;
			document.body.appendChild(link);
			link.click();
			window.URL.revokeObjectURL(url);
			document.body.removeChild(link);

			alert("Bookings exported successfully!");
		} catch (error) {
			console.error("Error exporting bookings:", error);
			alert("Error exporting bookings. Please try again later.");
		}
	};
	
	const handleStatusChange = async (e, id) => {
		const newStatus = e.target.value;

		await dispatch(updateBookingStatus(newStatus, id));
		setRefreshOnStatusUpdate((prev) => !prev);
	};

	return (
		<div className="booking-list">
			{/* Back Button */}
			<div className="back-button-container">
				<button
					className="back-button"
					onClick={() => navigate("/")}
				>
					<i className="bx bx-arrow-back"></i> Back to Dashboard
				</button>
			</div>
			
			{/* Header Section */}
			<header className="booking-list__header">
				<h1 className="booking-list__header-title">BOOKING LIST</h1>
				<div className="booking-list__regions">
					<a href="#" className="region-link active">
						{region}
					</a>
				</div>
			</header>

			{/* Summary Tabs */}
			<div className="summary-tabs">
				<a href="#" className="summary-tab">
					<span>BOOKINGS SUMMARY</span>
				</a>

				{regionCsvfilter === "uk" ? (
					<>
						<a href="#" className="summary-tab">
							<span>LONDON</span>
							<span className="summary-tab-number">
								{cityCounts.london}
							</span>
						</a>
						<a href="#" className="summary-tab">
							<span>MANCHESTER</span>
							<span className="summary-tab-number">
								{cityCounts.manchester}
							</span>
						</a>
						<a href="#" className="summary-tab">
							<span>LEEDS</span>
							<span className="summary-tab-number">
								{cityCounts.leeds}
							</span>
						</a>
					</>
				) : (
					<>
						<a href="#" className="summary-tab">
							<span>BEVERLY HILLS</span>
							<span className="summary-tab-number">
								{cityCounts.beverlyhills}
							</span>
						</a>
						<a href="#" className="summary-tab">
							<span>BOSTON</span>
							<span className="summary-tab-number">{cityCounts.boston}</span>
						</a>
						<a href="#" className="summary-tab">
							<span>SCOTTSDALE</span>
							<span className="summary-tab-number">
								{cityCounts.scottsdale}
							</span>
						</a>
						<a href="#" className="summary-tab">
							<span>MCLEAN VA</span>
							<span className="summary-tab-number">
								{cityCounts.mcleanva}
							</span>
						</a>
						<a href="#" className="summary-tab">
							<span>NEW YORK</span>
							<span className="summary-tab-number">
								{cityCounts.newyork}
							</span>
						</a>
					</>
				)}
			</div>

			{/* Filters Section */}
			<div className="view-settings">
				<button className="export-btn" onClick={handleExportAll}>
					<i className="bx bxs-file-export"></i>
					Export All
				</button>
				<div className="filter-options">
					<span>View settings:</span>
					<button className="filter-btn active">All</button>
					<button className="filter-btn">This week</button>
					<button className="filter-btn">This month</button>
					<button className="filter-btn">This year</button>
				</div>
			</div>

			{/* Booking Table */}
			<div className="booking-table-container">
				<table className="booking-table">
					<thead>
						<tr>
							<th>
								<div className="booking-table__label">
									<span>Stripe ID</span>
								</div>
							</th>
							<th>
								<MultiSelect
									options={formatOptions(
										bookings.map(
											(booking) => booking.CourseTitle
										)
									)}
									selectedValues={filters.title}
									onChange={(selected) =>
										handleFilterChange("title", selected)
									}
									showSearch={true}
									triggerElement={
										<div className="booking-table__label">
											<span>Title</span>
											<i className="bx bx-chevron-down"></i>
										</div>
									}
								/>
							</th>
							<th>
								<MultiSelect
									options={formatOptions(
										bookings.map(
											(booking) => booking.CourseDuration
										)
									)}
									selectedValues={filters.duration}
									onChange={(selected) =>
										handleFilterChange("duration", selected)
									}
									showSearch={true}
									triggerElement={
										<div className="booking-table__label">
											<span>Duration</span>
											<i className="bx bx-chevron-down"></i>
										</div>
									}
								/>
							</th>
							<th>
								<MultiSelect
									options={formatOptions(
										bookings.map(
											(booking) => (booking.CoursePrice / 100).toFixed(2)
										)
									)}
									selectedValues={filters.price}
									onChange={(selected) =>
										handleFilterChange("price", selected)
									}
									showSearch={true}
									triggerElement={
										<div className="booking-table__label">
											<span>Price</span>
											<i className="bx bx-chevron-down"></i>
										</div>
									}
								/>
							</th>
							<th>
								<MultiSelect
									options={formatOptions(
										bookings.map(
											(booking) => booking.CourseCity
										)
									)}
									selectedValues={filters.location}
									onChange={(selected) =>
										handleFilterChange("location", selected)
									}
									showSearch={true}
									triggerElement={
										<div className="booking-table__label">
											<span>Location</span>
											<i className="bx bx-chevron-down"></i>
										</div>
									}
								/>
							</th>
							<th>
								<MultiSelect
									options={formatOptions(
										bookings.map((booking) => booking.Date)
									)}
									selectedValues={filters.date}
									onChange={(selected) =>
										handleFilterChange("date", selected)
									}
									showSearch={true}
									triggerElement={
										<div className="booking-table__label">
											<span>Date</span>
											<i className="bx bx-chevron-down"></i>
										</div>
									}
								/>
							</th>
							<th>
								<MultiSelect
									options={formatOptions(
										bookings.map(
											(booking) => booking.CustomerName
										)
									)}
									selectedValues={filters.name}
									onChange={(selected) =>
										handleFilterChange("name", selected)
									}
									showSearch={true}
									triggerElement={
										<div className="booking-table__label">
											<span>Name</span>
											<i className="bx bx-chevron-down"></i>
										</div>
									}
								/>
							</th>
							<th>
								<div className="booking-table__label">
									<span>Contact Details</span>
								</div>
							</th>
							<th>
								<MultiSelect
									options={formatOptions(
										bookings.map(
											(booking) => booking.PurchaseDate
										)
									)}
									selectedValues={filters.purchasedate}
									onChange={(selected) =>
										handleFilterChange(
											"purchasedate",
											selected
										)
									}
									showSearch={true}
									triggerElement={
										<div className="booking-table__label">
											<span>Purchase Date</span>
											<i className="bx bx-chevron-down"></i>
										</div>
									}
								/>
							</th>
							<th>
								<div className="booking-table__label">
									<span>Paid Amount</span>
								</div>
							</th>
							<th>
								<div className="booking-table__label">
									<span>Places</span>
								</div>
							</th>
							<th>
								<div style={{ marginLeft: "-150px" }}>
									<MultiSelect
										options={[
											{ value: "New", label: "New" },
											{
												value: "Completed",
												label: "Completed",
											},
											{
												value: "On Hold",
												label: "On Hold",
											},
											{
												value: "Refunded",
												label: "Refunded",
											},
										]}
										selectedValues={filters.bookingstatus}
										onChange={(selected) =>
											handleFilterChange(
												"bookingstatus",
												selected
											)
										}
										showSearch={true}
										triggerElement={
											<div
												style={{ marginLeft: "150px" }}
												className="booking-table__label"
											>
												<span>Booking Status</span>
												<i className="bx bx-chevron-down"></i>
											</div>
										}
									/>
								</div>
							</th>
						</tr>
					</thead>
					<tbody>
						{isLoading ? (
							<tr>
								<td colSpan="12" style={{ textAlign: "center", padding: "20px" }}>
									Loading bookings data...
								</td>
							</tr>
						) : displayBookings.length === 0 ? (
							<tr>
								<td colSpan="12" style={{ textAlign: "center", padding: "20px" }}>
									No bookings found. Try changing your filters.
								</td>
							</tr>
						) : (
							displayBookings.map((booking, index) => (
								<tr key={index}>
									<td>{booking.StripeOrderId}</td>
									<td>{booking.CourseTitle}</td>
									<td>{booking.CourseDuration}</td>
									<td>{`${(booking.CoursePrice / 100).toFixed(2)}`}</td>
									<td>{booking.CourseCity}</td>
									<td>
										{booking.Date.replace(/with.*/, "")}
									</td>
									<td>{booking.CustomerName}</td>
									<td>
										<p>
											<i className="bx bx-envelope"></i>{" "}
											{booking.CustomerEmail}
										</p>

										<p>
											<i className="bx bx-map"></i>
											{(() => {
												const customerAddress =
													JSON.parse(
														booking.CustomerBillingAddress
													);
												return `${
													customerAddress.address
														?.line1
												}, ${
													customerAddress.address
														?.line2
														? customerAddress
																.address
																?.line2 + ", "
														: ""
												} ${
													customerAddress.address
														?.city
												}, ${
													customerAddress.address
														?.state
												} ${
													customerAddress.address
														?.postal_code
												}, ${
													customerAddress.address
														?.country
												}`;
											})()}
										</p>

										<p>
											<i className="bx bx-phone"></i>{" "}
											{booking.CustomerTelephone}
										</p>
										<p>{booking.experienceQualified}</p>

										<p>
											Confidence Level{" "}
											{booking.confidenceLevel}
										</p>
									</td>
									<td>{booking.PurchaseDate}</td>
									<td>
										Deposit: {booking.DepositeAmount / 100}
										.00
									</td>
									<td>{booking.Places}</td>
									<td>
										<select
											value={booking.BookingStatus ?? ""}
											onChange={(e) =>
												handleStatusChange(
													e,
													booking.StripeOrderId
												)
											}
										>
											<option value="New">New</option>
											<option value="Completed">
												Completed
											</option>
											<option value="On Hold">
												On Hold
											</option>
											<option value="Refunded">
												Refunded
											</option>
										</select>
									</td>
								</tr>
							))
						)}
					</tbody>
				</table>
				
				<div className="entries-info">
					Showing {displayBookings.length} of {bookings.length} entries
				</div>
			</div>

			{/* Footer */}
			<footer className="booking-list-footer">
				<p>&copy; 2024 SASSOON ACADEMY</p>
			</footer>
		</div>
	);
};

export default BookingList;
